import {useCallback, useMemo, useState} from 'react';

import {
  ConfirmationModal,
  Pagination,
  SearchBar,
  Select,
  TableSkeleton,
} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import type {AfriexCurrency} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

import ProcessorTable from './ProcessorTable';
import type {ProcessorSettingTarget, RemoveProcessorRequest} from './types';
import UpdateProcessorModal from './UpdateProcessorModal';
import useProcessorList from './useProcessorList';
import type {CorridorFilter} from './utils';
import {corridorFiltersOf} from './utils';

type Props = {
  target: ProcessorSettingTarget;
};

const PAGE_SIZE = 10;

const FILTER_OPTIONS: {value: CorridorFilter; label: string}[] = [
  {value: 'CONFIGURED', label: 'Configured'},
  {value: 'CURRENT_OFF', label: 'Current processor switched off'},
  {value: 'BACKUP_OFF', label: 'Backup processor switched off'},
  {value: 'NOT_CONFIGURED', label: 'Not configured'},
  {value: 'ALL', label: 'All currencies'},
];

const resolveEmptyStateMessage = (search: string, filter: CorridorFilter) => {
  if (search) {
    return `No currency matches "${search}".`;
  }
  if (filter === 'CONFIGURED') {
    return 'No processors configured for this audience yet. Choose "All currencies" to configure one.';
  }
  return 'No currencies match this filter.';
};

const ProcessorList = ({target}: Props) => {
  const {
    isLoading,
    handleUpdateProcessor,
    handleRemoveProcessor,
    countryCodeByCurrency,
    countryList,
    processorsAll,
    selectedCurrency,
    shouldShowUpdateForm,
    toggleProcessorUpdateForm,
    showProcessorUpdateForm,
    currencyList,
    capabilityMatrix,
  } = useProcessorList();

  // There is one currency document per supported asset (64 of them), but only a
  // handful are wired for any given audience. Default to the ones that are, and
  // keep the rest one filter away so a new corridor can still be configured.
  const [filter, setFilter] = useState<CorridorFilter>('CONFIGURED');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [isPageReset, setIsPageReset] = useState(false);

  const filtersByCode = useMemo(
    () =>
      new Map(
        (currencyList ?? []).map((c) => [
          c?.code,
          corridorFiltersOf(c, target, capabilityMatrix),
        ]),
      ),
    [currencyList, target, capabilityMatrix],
  );

  const filterCounts = useMemo(() => {
    const counts = Object.fromEntries(
      FILTER_OPTIONS.map(({value}) => [value, 0]),
    ) as Record<CorridorFilter, number>;
    filtersByCode.forEach((filters) =>
      filters.forEach((f) => {
        counts[f] += 1;
      }),
    );
    return counts;
  }, [filtersByCode]);

  const visibleCurrencies = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (currencyList ?? []).filter(
      (c) =>
        filtersByCode.get(c?.code)?.includes(filter) &&
        (!term ||
          c?.code?.toLowerCase().includes(term) ||
          c?.name?.toLowerCase().includes(term)),
    );
  }, [filter, search, currencyList, filtersByCode]);

  const pageItems = visibleCurrencies.slice(
    page * PAGE_SIZE,
    (page + 1) * PAGE_SIZE,
  );

  const resetPage = () => {
    setPage(0);
    setIsPageReset((prev) => !prev);
  };

  const handleSearch = (term: string) => {
    setSearch(term);
    resetPage();
  };

  const handleFilter = (value: CorridorFilter) => {
    setFilter(value);
    resetPage();
  };

  const [pendingRemoveRequest, setPendingRemoveRequest] =
    useState<RemoveProcessorRequest | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Intercepts all remove actions — just captures the request and opens the modal
  const requestRemoveProcessor = useCallback((req: RemoveProcessorRequest) => {
    setPendingRemoveRequest(req);
  }, []);

  // Called when the admin confirms inside the modal — fires the actual API call
  const confirmRemoveProcessor = async () => {
    if (!pendingRemoveRequest) {
      return;
    }
    setIsRemoving(true);
    await handleRemoveProcessor(pendingRemoveRequest);
    setIsRemoving(false);
    setPendingRemoveRequest(null);
  };

  const cancelRemoveProcessor = () => {
    setPendingRemoveRequest(null);
  };

  // Without this an unconfigured audience renders a bare table header, which
  // reads as a broken screen rather than an empty one.
  const emptyStateMessage = resolveEmptyStateMessage(search, filter);
  const loadedContent = visibleCurrencies.length ? (
    <ProcessorTable
      target={target}
      countryCodeByCurrency={countryCodeByCurrency}
      items={pageItems}
      countryList={countryList}
      capabilityMatrix={capabilityMatrix}
      showProcessorUpdateForm={showProcessorUpdateForm}
      onRemoveProcessor={requestRemoveProcessor}
    />
  ) : (
    <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
      {emptyStateMessage}
    </p>
  );

  const buildRemoveLabel = (req: RemoveProcessorRequest) => {
    const channelName = capitalizeWords(req.channel?.replace(/_/g, ' ') ?? '');
    const typeName = capitalizeWords(req.type?.replace(/_/g, ' ') ?? '');

    if (req.processorName) {
      // Removing from processors[] array
      return (
        <>
          Remove <strong>{req.processorName.toUpperCase()}</strong> from active
          processors for <strong>{channelName}</strong> ({typeName})?
        </>
      );
    }

    // Clearing currentProcessor — get its value from the currency object
    const currentProcessor =
      req.currency?.[req.target]?.[req.type]?.[req.channel]?.currentProcessor;

    return (
      <>
        Remove{' '}
        <strong>{(currentProcessor ?? 'this processor').toUpperCase()}</strong>{' '}
        as the current processor for <strong>{channelName}</strong> ({typeName}
        )?
      </>
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-5">
        <SearchBar
          handleSearch={handleSearch}
          handleAutoComplete={handleSearch}
          placeholder="Search currency"
          clearFilters={() => handleSearch('')}
        />
        <Select
          className="sm:w-72"
          label="Show"
          value={filter}
          options={FILTER_OPTIONS.map(({value, label}) => ({
            key: `${label} (${filterCounts[value]})`,
            value,
          }))}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleFilter(e.target.value as CorridorFilter)
          }
        />
      </div>

      <section className="pt-5 md:pt-5">
        {isLoading ? <TableSkeleton /> : loadedContent}
      </section>

      {visibleCurrencies.length ? (
        <Pagination
          onPageChange={
            ((params) =>
              setPage(params.page as number)) as PaginationProps['onPageChange']
          }
          total={visibleCurrencies.length}
          isSearch={Boolean(search.trim()) || filter !== 'CONFIGURED'}
          isPageReset={isPageReset}
        />
      ) : null}

      <UpdateProcessorModal
        target={target}
        isOpen={shouldShowUpdateForm}
        onClose={toggleProcessorUpdateForm}
        handleUpdate={handleUpdateProcessor}
        processors={processorsAll}
        capabilityMatrix={capabilityMatrix}
        currency={selectedCurrency as AfriexCurrency}
      />

      <ConfirmationModal
        isOpen={!!pendingRemoveRequest}
        title="Remove Processor"
        label={
          pendingRemoveRequest ? buildRemoveLabel(pendingRemoveRequest) : ''
        }
        confirmAction={confirmRemoveProcessor}
        confirmActionLabel="Remove"
        cancelAction={cancelRemoveProcessor}
        cancelActionLabel="Cancel"
        loading={isRemoving}
        loadingLabel="Removing..."
      />
    </>
  );
};

export default ProcessorList;
